import { useParams, Link, Navigate } from 'react-router';
import { FiArrowLeft, FiClock, FiCalendar } from 'react-icons/fi';
import { MDXProvider } from '@mdx-js/react';
import { Suspense, lazy, useMemo } from 'react';
import { blogPosts } from '~/components/landing/BlogCarousel';

// MDX component overrides for styling
const mdxComponents = {
  h1: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h1 className="text-3xl font-bold mt-8 mb-4" {...props} />
  ),
  h2: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h2 className="text-xl font-bold mt-8 mb-4" {...props} />
  ),
  h3: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h3 className="text-lg font-semibold mt-6 mb-3" {...props} />
  ),
  p: (props: React.HTMLAttributes<HTMLParagraphElement>) => (
    <p className="mb-4 leading-relaxed" {...props} />
  ),
  ul: (props: React.HTMLAttributes<HTMLUListElement>) => (
    <ul className="list-disc list-inside mb-4 space-y-2" {...props} />
  ),
  ol: (props: React.HTMLAttributes<HTMLOListElement>) => (
    <ol className="list-decimal list-inside mb-4 space-y-2" {...props} />
  ),
  li: (props: React.HTMLAttributes<HTMLLIElement>) => (
    <li className="ml-4" {...props} />
  ),
  blockquote: (props: React.HTMLAttributes<HTMLQuoteElement>) => (
    <blockquote className="border-l-4 border-primary pl-4 italic my-4 opacity-80" {...props} />
  ),
  code: (props: React.HTMLAttributes<HTMLElement>) => (
    <code className="bg-base-200 px-1.5 py-0.5 rounded text-sm" {...props} />
  ),
  pre: (props: React.HTMLAttributes<HTMLPreElement>) => (
    <pre className="bg-base-200 p-4 rounded-lg overflow-x-auto mb-4" {...props} />
  ),
  img: (props: React.ImgHTMLAttributes<HTMLImageElement>) => (
    <img className="rounded-lg my-6 max-w-full" {...props} />
  ),
  a: (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a className="text-primary hover:underline" {...props} />
  ),
  table: (props: React.TableHTMLAttributes<HTMLTableElement>) => (
    <div className="overflow-x-auto my-6 rounded-lg border border-base-300">
      <table className="table w-full" {...props} />
    </div>
  ),
  thead: (props: React.HTMLAttributes<HTMLTableSectionElement>) => (
    <thead className="bg-base-200" {...props} />
  ),
  tbody: (props: React.HTMLAttributes<HTMLTableSectionElement>) => (
    <tbody {...props} />
  ),
  tr: (props: React.HTMLAttributes<HTMLTableRowElement>) => (
    <tr className="border-b border-base-200 last:border-0" {...props} />
  ),
  th: (props: React.ThHTMLAttributes<HTMLTableCellElement>) => (
    <th className="font-semibold text-left px-4 py-3 bg-base-200" {...props} />
  ),
  td: (props: React.TdHTMLAttributes<HTMLTableCellElement>) => (
    <td className="px-4 py-3 align-top" {...props} />
  ),
};

// Dynamic import for MDX/MD files
const blogModules = import.meta.glob('../../content/blog/*.{mdx,md}');

export default function BlogArticle() {
  const { slug } = useParams<{ slug: string }>();

  // Find the post metadata
  const post = blogPosts.find((p) => p.slug === slug);

  // Dynamically load the MDX/MD component
  const MDXContent = useMemo(() => {
    // Try .mdx first, then .md
    const mdxPath = `../../content/blog/${slug}.mdx`;
    const mdPath = `../../content/blog/${slug}.md`;
    const loader = blogModules[mdxPath] || blogModules[mdPath];

    if (!loader) {
      return null;
    }

    return lazy(() => loader() as Promise<{ default: React.ComponentType }>);
  }, [slug]);

  // If post not found, redirect to blog index
  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="h-screen overflow-y-auto bg-base-100">
      {/* Header */}
      <header className="py-6 px-8 lg:px-16 border-b border-base-200">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-primary">
            Gamgee Analytics
          </Link>
          <Link to="/blog" className="btn btn-ghost btn-sm gap-2">
            <FiArrowLeft className="w-4 h-4" />
            All Articles
          </Link>
        </div>
      </header>

      {/* Article */}
      <article className="py-12 px-8 lg:px-16">
        <div className="max-w-4xl mx-auto">
          {/* Article Header */}
          <header className="mb-8">
            <div className="flex items-center gap-3 text-sm mb-4">
              <span className="badge badge-primary">{post.category}</span>
              <span className="flex items-center gap-1 opacity-60">
                <FiClock className="w-4 h-4" />
                {post.readTime}
              </span>
              <span className="flex items-center gap-1 opacity-60">
                <FiCalendar className="w-4 h-4" />
                {post.publishedAt
                  ? new Date(post.publishedAt).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
                  : new Date().toLocaleDateString()}
              </span>
            </div>

            <h1 className="text-2xl lg:text-3xl font-bold mb-4">
              {post.title}
            </h1>

            <p className="text-base opacity-70">
              {post.excerpt}
            </p>

            {/* Author */}
            {post.author && (
              <a
                href={post.author.linkedIn}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 mt-6 pt-6 border-t border-base-200 hover:opacity-80 transition-opacity"
              >
                {post.author.avatar ? (
                  <img
                    src={post.author.avatar}
                    alt={post.author.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-lg font-semibold text-primary">
                      {post.author.name.charAt(0)}
                    </span>
                  </div>
                )}
                <div>
                  <p className="font-semibold">{post.author.name}</p>
                  {post.author.role && (
                    <p className="text-sm opacity-60">{post.author.role}</p>
                  )}
                </div>
              </a>
            )}
          </header>

          {/* Featured Image */}
          {post.image ? (
            <figure className="mb-8">
              <img
                src={post.image}
                alt={post.title}
                className="w-full rounded-xl"
              />
            </figure>
          ) : (
            <figure className="h-64 lg:h-80 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-xl flex items-center justify-center mb-8">
              <span className="text-6xl opacity-30">
                {post.category === 'Strategy' && '📊'}
                {post.category === 'Conversion' && '🛒'}
                {post.category === 'Analytics' && '📈'}
                {post.category === 'Operations' && '🚚'}
                {post.category === 'Marketing' && '📈'}
                {post.category === 'Retention' && '🔄'}
              </span>
            </figure>
          )}

          {/* Article Content */}
          <div className="prose max-w-none">
            {MDXContent ? (
              <MDXProvider components={mdxComponents}>
                <Suspense fallback={<div className="loading loading-spinner loading-lg" />}>
                  <MDXContent />
                </Suspense>
              </MDXProvider>
            ) : (
              <div className="text-center py-12">
                <p className="text-lg opacity-70">
                  This article is coming soon. Check back later!
                </p>
              </div>
            )}
          </div>

          {/* Back to Blog */}
          <div className="mt-12 pt-8 border-t border-base-200">
            <Link to="/blog" className="btn btn-outline gap-2">
              <FiArrowLeft className="w-4 h-4" />
              Back to All Articles
            </Link>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="py-8 px-8 lg:px-16 bg-base-200 text-center">
        <p className="text-sm opacity-60">
          &copy; {new Date().getFullYear()} Gamgee Analytics. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
